"use client";

import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  Input,
} from "@/ui";
import { useOnboarding } from "@/lib/onboarding/OnboardingContext";
import styles from "./BusinessEmailDialog.module.css";

/**
 * Business email prompt.
 *
 * A social registration can arrive on a personal address (gmail, outlook, …),
 * whose domain tells us nothing about the recruiter's business. Onboarding
 * research is held back until we have a work address, so the status bar nudges
 * for one and opens this without leaving the page.
 *
 * The work email is stored alongside the login email — it does not replace it.
 * Saving it releases the research run, which starts as soon as the recruiter
 * refetches without `needs_business_email`.
 */
export default function BusinessEmailDialog() {
  const { emailPromptOpen, closeEmailPrompt, saveBusinessEmail } = useOnboarding();

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (saving || !email.trim()) return;

    setSaving(true);
    setError(null);

    try {
      await saveBusinessEmail(email.trim());
    } catch (e: any) {
      setError(
        e?.response?.data?.errors?.business_email?.[0] ||
          e?.response?.data?.message ||
          "Could not save your work email. Please try again.",
      );
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={emailPromptOpen}
      onOpenChange={(open: boolean) => {
        if (!open && !saving) closeEmailPrompt();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="sm">
          {/* The submit button lives in the footer, outside the body, so the
              form wraps the whole dialog to keep Enter-to-submit working. */}
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>What&apos;s your work email?</DialogTitle>
            </DialogHeader>

            <DialogBody>
              <p className={styles.intro}>
                You signed up with a personal email address. We use your work
                email&apos;s domain to research your business and set up your
                account.
              </p>

              {error && (
                <div className={styles.error}>
                  <Alert variant="error">{error}</Alert>
                </div>
              )}

              <Input
                type="email"
                label="Work email"
                placeholder="you@yourbusiness.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                fullWidth
                disabled={saving}
              />

              <p className={styles.hint}>
                This is stored alongside your login email — you&apos;ll still
                sign in the same way.
              </p>
            </DialogBody>

            <DialogFooter>
              <Button
                type="button"
                variant="tertiary"
                disabled={saving}
                onClick={closeEmailPrompt}
              >
                Skip for now
              </Button>
              <Button
                type="submit"
                loading={saving}
                disabled={!email.trim() || saving}
              >
                Continue
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
